import { useQuery } from "@tanstack/react-query";
import { CLIRNET_API } from "./endpoints.js";
import { apiClient } from "./axios.js";


// POST cme/comment/add: logs an admin chat message in the DB. parentId is 0 for the admin's
// own message, and for a reply the comment_id of the HCP message it answers. isApproved /
// pinned are always true / false - an admin message goes out approved and unpinned.
// Answers { status: "success", msg, data: { commentId } }: that commentId is the message's
// comment_id on chat-sent.
export async function addAdminChatComment({ parentId = 0, sessionId, message }) {
  const res = await apiClient.post(CLIRNET_API.CME_COMMENT_ADD, {
    parentId: Number.isFinite(Number(parentId)) ? Number(parentId) : parentId,
    sessionId: String(sessionId ?? ""),
    message,
    isApproved: true,
    pinned: false,
  });
  return res.data;
}

// Dynamic per-vendor credentials: GET /cme/hostvendor/{vendor}/{meetingNumber}.
// Returns the RAW `data` object - each meeting vendor maps its own fields
// (zoom: zoom_signature/zoom_zak, realtimekit: realtimekit_token, 100ms: hundredms_token),
// so this helper must stay vendor-agnostic.
export async function fetchHostVendorCreds(vendor, meetingNumber) {
  const res = await apiClient.get(CLIRNET_API.HOST_VENDOR(vendor, meetingNumber));
  return res.data?.data || {};
}

export async function fetchAdminUserDetails() {
  const res = await apiClient.get(CLIRNET_API.USER_DETAILS);
  return res.data;
}

export function useAdminUserDetails(options = {}) {
  return useQuery({
    queryKey: ["admin", "userDetails"],
    queryFn: fetchAdminUserDetails,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });
}

// POST cme/comment/pinned { commentId, pinned }: a chat message's pin state in the DB.
// comment_id: that message's comment_id - an HCP message's or an admin message's own (the
// API's body field for it is named commentId). pinned: 1 = pinned, 0 = not.
export async function setCmeCommentPinned(comment_id, pinned) {
  const res = await apiClient.post(CLIRNET_API.CME_COMMENT_PINNED, {
    commentId: Number.isFinite(Number(comment_id)) ? Number(comment_id) : comment_id,
    pinned: pinned ? 1 : 0,
  });
  return res.data;
}

// POST cme/comment/update { commentId, status }. comment_id: the chat message's comment_id
// (from sessions/submit/session/comment) - the API's body field for it is named commentId.
export async function updateCmeCommentStatus(comment_id, status) {
  const payload = {
    commentId: Number.isFinite(Number(comment_id)) ? Number(comment_id) : comment_id,
    status: status ? 1 : 0,
  };
  const endpoint = CLIRNET_API.CME_COMMENT_UPDATE;
  const res = await apiClient.post(endpoint, payload);
  return res.data;
}

// pollId is the interactive quiz's own poll id (e.g. 845), not the session id.
export async function fetchQuizResult(pollId) {
  const res = await apiClient.get(CLIRNET_API.QUIZ_result(pollId));
  return res.data;
}

// POST cme/streampublish/{hosting vendor}. source: the stream link the admin typed for the
// "Web Page" streaming vendor (a src the admin enters instead of a link the backend
// generates) - sent only when there is one.
export async function generateStreamingLink(vendorType, { session_id, metting_id, vendor_id, source }) {
  const endpoint = CLIRNET_API.STREAM_PUBLISH(vendorType);
  const body = { session_id, metting_id, vendor_id };
  if (source) body.source = source;
  const res = await apiClient.post(endpoint, body);
  return res.data;
}

function normalizeStreamingVendors(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];
  return raw
    .map((item) => {
      if (item == null || typeof item !== "object") return null;
      const vendorid = item.vendor_id ?? item.vendorid ?? item.id;
      const vendor = item.name ?? item.vendor ?? item.vendor_name;
      if (vendorid === undefined || vendorid === null || vendorid === "" || vendor == null || vendor === "") return null;
      return { vendor: String(vendor), vendorid };
    })
    .filter(Boolean);
}

async function fetchStreamingVendors(hostingVendor) {
  const res = await apiClient.get(CLIRNET_API.VENDOR_LIST(hostingVendor));
  return normalizeStreamingVendors(res.data);
}

// GET cme/streaming-creative/{tenant} -> { data: [{ id, level, url }] }. `level` is the
// creative's display name. Items without a url are dropped (nothing to preview or publish).
function normalizeStreamingCreatives(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];
  return raw
    .map((item, i) => {
      if (item == null || typeof item !== "object") return null;
      const url = typeof item.url === "string" ? item.url.trim() : "";
      if (!url) return null;
      const name = item.level ?? item.label ?? item.name ?? item.title;
      const label = typeof name === "string" && name.trim() ? name.trim() : `Creative ${i + 1}`;
      return { id: String(item.id ?? url), label, url };
    })
    .filter(Boolean);
}

export async function fetchStreamingCreatives(tenant) {
  const res = await apiClient.get(CLIRNET_API.STREAMING_CREATIVE(tenant));
  return normalizeStreamingCreatives(res.data);
}

// tenant: the session's tenant (cme-common data.detail.tenet, e.g. "clirnet") - the dynamic
// segment of the list URL and part of the cache key, so each tenant's list is cached apart.
// No request is made until it is known.
export function useStreamingCreatives(tenant, options = {}) {
  const { enabled = true, ...rest } = options;
  return useQuery({
    queryKey: ["admin", "streaming-creatives", tenant],
    queryFn: () => fetchStreamingCreatives(tenant),
    enabled: Boolean(tenant) && enabled,
    retry: 1,
    refetchOnWindowFocus: false,
    ...rest,
  });
}

// `hostingVendor` is the canonical hosting vendor key (see normalizeVendor in
// meeting/vendors.js) — it is the dynamic segment of the list URL, and part of
// the cache key so each vendor's list is cached separately. No request is made
// until it is known.
export function useStreamingVendors(hostingVendor, options = {}) {
  const { enabled = true, ...rest } = options;
  return useQuery({
    queryKey: ["admin", "streaming-vendors", hostingVendor],
    queryFn: () => fetchStreamingVendors(hostingVendor),
    enabled: Boolean(hostingVendor) && enabled,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...rest,
  });
}
